import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  getDoc,
  updateDoc,
  deleteField,
  writeBatch,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { Observable } from 'rxjs';
import { Pago, PagoEstado, PagoMetodo } from '../models/pago.model';
import { Servicio } from '../models/servicio.model';

export interface GeneracionPagosResultadoServicio {
  servicioId:     string;
  servicioNombre: string;
  periodo:        string;
  creados:        number;
  omitidos:       number;
  errores:        number;
  detalleErrores: { residenteUid: string; mensaje: string }[];
}

export interface GeneracionPagosResultado {
  fechaObjetivo: Date;
  servicios:     GeneracionPagosResultadoServicio[];
  totales:       { creados: number; omitidos: number; errores: number };
}

@Injectable({ providedIn: 'root' })
export class PagosService {
  private firestore   = inject(Firestore);
  private storage     = inject(Storage);

  private col(residencialId: string) {
    return collection(this.firestore, `residenciales/${residencialId}/pagos`);
  }

  private docRef(residencialId: string, pagoId: string) {
    return doc(this.firestore, `residenciales/${residencialId}/pagos/${pagoId}`);
  }

  // ── Lecturas ─────────────────────────────────────────────────────────────────

  getPagoById(residencialId: string, pagoId: string): Observable<Pago> {
    return docData(this.docRef(residencialId, pagoId), { idField: 'id' }) as Observable<Pago>;
  }

  getPagosPorResidente(residencialId: string, residenteUid: string): Observable<Pago[]> {
    return collectionData(
      query(
        this.col(residencialId),
        where('residenteUid', '==', residenteUid),
        orderBy('creadoEn', 'desc')
      ),
      { idField: 'id' }
    ) as Observable<Pago[]>;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  // Convierte servicio.diaPago + periodo en el Timestamp de vencimiento.
  // Semanal usa ISO 8601: diaPago 1=lun … 7=dom.
  calcularFechaVencimiento(servicio: Servicio, periodo: string): Timestamp {
    const { frecuencia, diaPago } = servicio;
    let fecha: Date;

    if (frecuencia === 'mensual') {
      const [year, month] = periodo.split('-').map(Number);
      fecha = new Date(year, month - 1, diaPago);

    } else if (frecuencia === 'quincenal') {
      const parts    = periodo.split('-');
      const year     = Number(parts[0]);
      const month    = Number(parts[1]) - 1;
      const quincena = parts[2];
      const dia      = quincena === 'Q1' ? diaPago : diaPago + 15;
      fecha = new Date(year, month, dia);

    } else if (frecuencia === 'semanal') {
      const [yearStr, weekStr] = periodo.split('-');
      const year = Number(yearStr);
      const week = Number(weekStr.replace('W', ''));

      // Jan 4 siempre cae en la semana ISO 1
      const jan4    = new Date(year, 0, 4);
      const jan4Day = jan4.getDay() || 7;
      const mondayW1 = new Date(jan4);
      mondayW1.setDate(jan4.getDate() - (jan4Day - 1));

      fecha = new Date(mondayW1);
      fecha.setDate(mondayW1.getDate() + (week - 1) * 7 + (diaPago - 1));

    } else if (frecuencia === 'unica') {
      const [year, month, day] = periodo.split('-').map(Number);
      fecha = new Date(year, month - 1, day);

    } else {
      throw new Error(`Frecuencia no soportada: ${frecuencia}`);
    }

    // El vencimiento es el día siguiente al diaPago
    fecha.setDate(fecha.getDate() + 1);
    return Timestamp.fromDate(fecha);
  }

  // ── Admin ─────────────────────────────────────────────────────────────────────

  async confirmarPago(
    residencialId: string,
    pagoId: string,
    adminUid: string,
    overrides?: { metodoPago?: PagoMetodo; idCuenta?: string }
  ): Promise<void> {
    try {
      const ref  = this.docRef(residencialId, pagoId);
      const snap = await getDoc(ref);
      if (!snap.exists()) throw new Error(`Pago ${pagoId} no encontrado`);

      const pago = snap.data() as Omit<Pago, 'id'>;
      await updateDoc(ref, {
        estado:            'confirmado' as PagoEstado,
        confirmadoPor:     adminUid,
        fechaConfirmacion: serverTimestamp(),
        metodoPago:        overrides?.metodoPago ?? pago.metodoPago ?? ('transferencia' as PagoMetodo),
        ...(overrides?.idCuenta ? { idCuenta: overrides.idCuenta } : {}),
      });
    } catch (e) {
      throw new Error(`Error al confirmar pago: ${(e as Error).message}`);
    }
  }

  async rechazarPago(
    residencialId: string,
    pagoId: string,
    adminUid: string,
    motivo: string,
    overrides?: { metodoPago?: PagoMetodo; idCuenta?: string }
  ): Promise<void> {
    try {
      await updateDoc(this.docRef(residencialId, pagoId), {
        estado:            'rechazado' as PagoEstado,
        confirmadoPor:     adminUid,
        fechaConfirmacion: serverTimestamp(),
        motivoRechazo:     motivo,
        ...(overrides?.metodoPago ? { metodoPago: overrides.metodoPago } : {}),
        ...(overrides?.idCuenta   ? { idCuenta:   overrides.idCuenta   } : {}),
      });
    } catch (e) {
      throw new Error(`Error al rechazar pago: ${(e as Error).message}`);
    }
  }

  /**
   * Cambio manual de estado por el admin (funcionalidad de contingencia).
   * Permite corregir un pago mal confirmado/rechazado o cancelar uno generado por error.
   * A diferencia de confirmar/rechazar, no depende del comprobante ni del método de pago.
   *
   * Manejo de campos de auditoría según el estado destino:
   *  - pendiente / enRevision → limpia confirmadoPor, fechaConfirmacion y motivoRechazo.
   *  - confirmado             → sella confirmadoPor + fechaConfirmacion, limpia motivoRechazo.
   *  - rechazado              → sella confirmadoPor + fechaConfirmacion + motivoRechazo.
   *  - cancelado              → sella confirmadoPor + fechaConfirmacion como traza, conserva el resto.
   */
  async cambiarEstadoPago(
    residencialId: string,
    pagoId: string,
    adminUid: string,
    nuevoEstado: PagoEstado,
    opts?: { motivo?: string }
  ): Promise<void> {
    try {
      const update: Record<string, unknown> = { estado: nuevoEstado };

      switch (nuevoEstado) {
        case 'pendiente':
        case 'enRevision':
          update['confirmadoPor']     = deleteField();
          update['fechaConfirmacion'] = deleteField();
          update['motivoRechazo']     = deleteField();
          break;
        case 'confirmado':
          update['confirmadoPor']     = adminUid;
          update['fechaConfirmacion'] = serverTimestamp();
          update['motivoRechazo']     = deleteField();
          break;
        case 'rechazado':
          update['confirmadoPor']     = adminUid;
          update['fechaConfirmacion'] = serverTimestamp();
          update['motivoRechazo']     = opts?.motivo ?? '';
          break;
        case 'cancelado':
          update['confirmadoPor']     = adminUid;
          update['fechaConfirmacion'] = serverTimestamp();
          break;
        default:
          throw new Error(`Estado destino no soportado: ${nuevoEstado}`);
      }

      await updateDoc(this.docRef(residencialId, pagoId), update);
    } catch (e) {
      throw new Error(`Error al cambiar estado del pago: ${(e as Error).message}`);
    }
  }

  async generarPagosPorPeriodos(
    residencialId: string,
    adminUid: string,
    seleccion: { servicio: Servicio; periodo: string }[],
    existeLocalmenteFn?: (pagoId: string) => boolean
  ): Promise<GeneracionPagosResultado> {
    const serviciosResultado: GeneracionPagosResultadoServicio[] = [];

    type Pendiente = {
      pagoId:       string;
      datos:        Omit<Pago, 'id' | 'creadoEn'>;
      resultadoIdx: number;
      residenteUid: string;
    };
    const pendientes: Pendiente[] = [];

    // Fase 1 — chequeo local O(1): omite los pagos que ya existen en el state
    for (const { servicio, periodo } of seleccion) {
      const fechaVencimiento = this.calcularFechaVencimiento(servicio, periodo);

      const resultado: GeneracionPagosResultadoServicio = {
        servicioId:     servicio.id,
        servicioNombre: servicio.nombre,
        periodo,
        creados:        0,
        omitidos:       0,
        errores:        0,
        detalleErrores: [],
      };
      serviciosResultado.push(resultado);
      const idx = serviciosResultado.length - 1;

      for (const entry of (servicio.residentesAsociados ?? []).filter(e => e.aplicar)) {
        const pagoId = `${entry.residenteUid}_${servicio.id}_${periodo}`;

        if (existeLocalmenteFn?.(pagoId)) {
          resultado.omitidos++;
          continue;
        }

        pendientes.push({
          pagoId,
          datos: {
            residenteUid:   entry.residenteUid,
            servicioId:     servicio.id,
            servicioNombre: servicio.nombre,
            servicioIcono:  servicio.icono,
            monto:          entry.monto,
            moneda:         servicio.moneda,
            periodo,
            estado:         'pendiente',
            fechaVencimiento,
            creadoPor:      adminUid,
          },
          resultadoIdx: idx,
          residenteUid: entry.residenteUid,
        });
      }
    }

    // Fase 2 — escritura por lotes de 500
    const CHUNK = 500;
    for (let i = 0; i < pendientes.length; i += CHUNK) {
      const chunk = pendientes.slice(i, i + CHUNK);
      const batch = writeBatch(this.firestore);
      for (const p of chunk) {
        batch.set(
          doc(this.firestore, `residenciales/${residencialId}/pagos/${p.pagoId}`),
          { ...p.datos, creadoEn: serverTimestamp() }
        );
      }
      try {
        await batch.commit();
        for (const p of chunk) serviciosResultado[p.resultadoIdx].creados++;
      } catch (e) {
        for (const p of chunk) {
          serviciosResultado[p.resultadoIdx].errores++;
          serviciosResultado[p.resultadoIdx].detalleErrores.push({
            residenteUid: p.residenteUid,
            mensaje:      (e as Error).message,
          });
        }
      }
    }

    const totales = serviciosResultado.reduce(
      (acc, s) => ({ creados: acc.creados + s.creados, omitidos: acc.omitidos + s.omitidos, errores: acc.errores + s.errores }),
      { creados: 0, omitidos: 0, errores: 0 }
    );

    return { fechaObjetivo: new Date(), servicios: serviciosResultado, totales };
  }

  // ── Residente ─────────────────────────────────────────────────────────────────

  async subirComprobante(
    residencialId: string,
    pagoId:        string,
    urls:          string[],
    fechaPago:     Timestamp,
    idCuenta?:     string,
    metodoPago:    PagoMetodo = 'transferencia',
    notas?:        string
  ): Promise<void> {
    try {
      await updateDoc(this.docRef(residencialId, pagoId), {
        estado:       'enRevision' as PagoEstado,
        comprobantes: urls,
        fechaPago,
        metodoPago,
        ...(idCuenta !== undefined ? { idCuenta } : {}),
        ...(notas    !== undefined ? { notas    } : {}),
      });
    } catch (e) {
      throw new Error(`Error al subir comprobante: ${(e as Error).message}`);
    }
  }

  async subirComprobantes(residencialId: string, pagoId: string, files: File[]): Promise<string[]> {
    const urls: string[] = [];
    for (const file of files) {
      const ext        = file.name.split('.').pop() ?? 'jpg';
      const storageRef = ref(this.storage, `residenciales/${residencialId}/pagos/${pagoId}/${Date.now()}.${ext}`);
      await uploadBytes(storageRef, file);
      urls.push(await getDownloadURL(storageRef));
    }
    return urls;
  }

  async actualizarPago(
    residencialId: string,
    pagoId: string,
    cambios: Partial<Omit<Pago, 'id' | 'creadoEn' | 'residenteUid' | 'servicioId' | 'servicioNombre' | 'servicioIcono' | 'monto' | 'moneda' | 'periodo'>>
  ): Promise<void> {
    try {
      await updateDoc(this.docRef(residencialId, pagoId), { ...cambios });
    } catch (e) {
      throw new Error(`Error al actualizar pago: ${(e as Error).message}`);
    }
  }
}
